import moment from 'moment'
import { get } from 'lodash'
import { AuthorizeResult } from 'react-native-app-auth'
import { Apollo } from '@config/apollo'
import Device from '@config/device'
import User from '@utils/user'
import Storage from '@utils/storage'
import Caching from '@utils/caching'
import HealthKit from '@services/HealthKit'
import KetoMojo from '@services/KetoMojo'
import Model from '@models/model'
import Analytics, { CustomEventTypes } from '@config/analytics'

import reducers from '@src/models/app.reducers'
import { transform } from '@src/transforms/clientConfig'
import { DEFAULT_CLIENT_CONFIG } from '@src/fallbacks/fetchClientConfig'
import { AppStoreState, Login } from '@src/models/app.types'
import {
  calendarDateSelector,
  healthKitIgnoreSelector,
  healthKitSourceIgnoreSelector,
  uiStatesSelector,
} from '@src/selectors/app'
import {
  healthLastSyncedSelector,
  ketoMojoLastSyncedSelector,
  ketoMojoUserSelector,
} from '@src/selectors/integrations'
import { darkModeStoreStateSelector } from '@src/selectors/settings'
import { getLastTimestamp } from '@src/services/ThirdPartyHealth'
import Stripe from '@config/stripe'
import { Reading } from '@src/services/KetoMojo/ketoMojo'
import { HealthDataSampleResult } from '@src/services/ThirdPartyHealth/thirdPartyHealth'
import { SAVE_SURVEY_RESPONSE } from '@src/graphql/saveSurveyResponse'
import { CREATE_DIRECT_UPLOAD } from '@src/graphql/createDirectUpload'
import { CREATE_FILE_FROM_SIGNED_ID } from '@src/graphql/createFileFromSignedId'
import { DELETE_FILE } from '@src/graphql/deleteFile'
import StreamChat from '@src/services/StreamChat'
import { UPSERT_UI_STATE } from '@src/graphql/upsertUiState'
import { BillingProductCategory, DexcomConnectionKind, UiState } from '@src/types'
import { UpsertUiStateMutation } from '@src/graphql/upsertUiState.generated'
import { UPSERT_DEXCOM_CONNECTION } from '@src/graphql/upsertDexcomConnection'
import { DELETE_DEXCOM_CONNECTION } from '@src/graphql/deleteDexcomConnection'
import {
  UpsertDexcomConnectionMutation,
  UpsertDexcomConnectionMutationVariables,
} from '@src/graphql/upsertDexcomConnection.generated'
import { SEND_CHAT_CONVERSATION_MESSAGE } from '@src/graphql/sendChatConversationMessage'
import { CREATE_CHAT_CONVERSATION_NOTE } from '@src/graphql/createChatConversationNote'
// eslint-disable-next-line max-len
import { CONFIRM_LIBRE_INDIRECT_SYNC } from '@src/screens/OwnSensorOnboarding/graphql/confirmLibreIndirectSync'
import { FETCH_LATEST_SURVEY_BY_KIND } from '@src/graphql/fetchLatestSurveyByKind'
import {
  DeleteDexcomConnectionMutation,
  DeleteDexcomConnectionMutationVariables,
} from '@src/graphql/deleteDexcomConnection.generated'
import { SAVE_SURVEY_RESPONSES } from '@src/graphql/saveSurveyResponses'
import { DarkMode } from '@src/screens/Settings/models/settings.types'
import { allEventsCalendarDateSelector } from '@src/screens/Events/models/allEvents.selectors'
import { FETCH_PRODUCT } from '@src/graphql/fetchProduct'
import { SIGN_IN_USER } from '../graphql/signInUser'
import { SIGN_IN_THIRD_PARTY_USER } from '../graphql/signInThirdPartyUser'
import { UPDATE_HEALTH_DATA } from '../graphql/updateHealthData'
import { UPDATE_KETO_MOJO_DATA } from '../graphql/updateKetoMojoData'
import { FETCH_DATA_EXPORT } from '../graphql/fetchDataExport'
import { FETCH_CLIENT_CONFIG } from '../graphql/fetchClientConfig'
import { FETCH_CALENDAR_DATA } from '../graphql/fetchCalendarData'
import { SEND_USER_INVITE } from '../graphql/sendUserInvite'
import { FETCH_QUESTIONNAIRE } from '../graphql/fetchQuestionnaire'
import { START_QUESTIONNAIRE } from '../graphql/startQuestionnaire'
import { COMPLETE_SURVEY } from '../graphql/completeSurvey'
import { FETCH_USER_FILE } from '../graphql/fetchUserFile'
import { FETCH_USER_FILES } from '../graphql/fetchUserFiles'
import { FETCH_PRODUCTS } from '../graphql/fetchProducts'
import { WORKFLOW_RUN } from '../graphql/workflowRun'
import {
  initCalendar,
  processCalendarData,
  processDateRangePayload,
  todayDateRangePayload,
  validateDateRange,
} from './helper'
import { unlessHasOpenedAppToday } from './utils'

const PushNotification = Device.web ? null : require('react-native-push-notification')

const loginDefaults = { success: false, user: null, features: [] }

export default class App {
  namespace = 'app'

  state: AppStoreState = {
    ...Model.defaultState,
    login: loginDefaults,
    lastOpened: moment().toISOString(),
    lastScanned: moment().toISOString(),
    calendar: initCalendar(),
    clientConfig: transform(DEFAULT_CLIENT_CONFIG).clientConfig,
    onboarding: {
      isInProgress: false,
      skipped: false,
    },
    skippedSurveys: {},
    products: [],
    productsByCategory: {},
    lastRequestStatus: null,
    pendingEventsRefresh: false,
  }

  effects = {
    login: Model.buildEffect({
      name: `${this.namespace}/login`,
      query: SIGN_IN_USER,
      dataPath: 'signinUser',
      isSuccess: (response: any) => response?.success,
      *onSuccess(login: Login, { call }: any) {
        yield call(User.onLogin, login)
      },
      reducers: [
        {
          name: 'updateLoginState',
        },
      ],
    }),

    logout: Model.buildEffect({
      name: `${this.namespace}/logout`,
      *onSuccess(_: any, { call, put, select }: any) {
        console.log('app/logout onSuccess Called!')
        Storage.remove(Storage.MFA_TOKEN)
        Storage.remove(Storage.ONBOARDING_FLOW_TYPE_SELECTED_KEY)
        Storage.remove(Storage.ONBOARDING_COACHING_KINDS_KEY)
        Storage.remove(Storage.ONBOARDING_INSURANCE_PROVIDER_KEY)
        Storage.remove(Storage.ONBOARDING_GOAL_KEY)

        if (!Device.web) {
          PushNotification.setApplicationIconBadgeNumber(0)
        }

        const darkMode: DarkMode = yield select(darkModeStoreStateSelector)

        yield call(User.onLogout)
        Apollo.resetEndpoint()
        Stripe.configure(Apollo.endpointEnv)
        StreamChat.configure(Apollo.endpointEnv)

        yield put({ type: 'reset' })
        yield put({ type: 'settings/updateSettings', payload: { darkMode } })
      },
      reducers: [
        {
          name: 'updateAppState',
          payload: { login: loginDefaults },
        },
      ],
    }),

    thirdPartyLogin: Model.buildEffect({
      name: `${this.namespace}/thirdPartyLogin`,
      query: SIGN_IN_THIRD_PARTY_USER,
      dataPath: 'signInThirdPartyUser',
      isSuccess: (response: any) => response?.success,
      *onSuccess(login: Login, { call }: any) {
        yield call(User.onLogin, login)
      },
      reducers: [
        {
          name: 'updateLoginState',
        },
      ],
    }),

    changeDateRange: Model.buildEffect({
      name: `${this.namespace}/changeDateRange`,
      validation: validateDateRange,
      reducers: [
        {
          name: 'updateCalendarState',
          payload: processDateRangePayload,
        },
        {
          // keeps all events calendar in sync with app calendar
          name: 'allEvents/updateCalendarState',
          payload: processDateRangePayload,
        },
      ],
    }),

    resetCalendars: Model.buildEffect({
      name: `${this.namespace}/resetCalendars`,
      reducers: [
        {
          name: 'updateCalendarState',
          unless: unlessHasOpenedAppToday,
          payload: todayDateRangePayload,
        },
        {
          name: 'insights/updateCalendarState',
          unless: unlessHasOpenedAppToday,
          payload: todayDateRangePayload,
        },
        {
          name: 'allEvents/updateCalendarState',
          unless: unlessHasOpenedAppToday,
          payload: todayDateRangePayload,
        },
        {
          name: 'plan/updateCalendarState',
          unless: unlessHasOpenedAppToday,
          payload: todayDateRangePayload,
        },
      ],
    }),

    updateHealthData: Model.buildEffect({
      name: `${this.namespace}/updateHealthData`,
      dataPath: 'updateHealthData',
      query: UPDATE_HEALTH_DATA,
      *variables(_: any, { call, select }: any) {
        const lastSyncedHealth: string = yield select(healthLastSyncedSelector)

        const sourceIgnore: string[] = yield select(healthKitSourceIgnoreSelector)
        const ignore: string[] = yield select(healthKitIgnoreSelector)

        const samples: HealthDataSampleResult = yield call(HealthKit.getSamples, {
          lastSynced: lastSyncedHealth,
          ignore,
          sourceIgnore,
        })

        return { healthData: samples }
      },
      isSuccess: (response: any) => response?.success,
      onSuccess() {
        Analytics.track(CustomEventTypes.HealthKitUploadCompleted)
      },
      onFailure() {
        Analytics.track(CustomEventTypes.HealthKitUploadFailed)
      },
      reducers: [
        {
          name: 'settings/updateIntegrationsSyncSettings',
          unless: (_: any, { variables }: { variables: { healthData: HealthDataSampleResult } }) =>
            !getLastTimestamp(variables.healthData),
          payload: (
            _: any,
            { variables }: { variables: { healthData: HealthDataSampleResult } },
          ) => ({
            lastSyncedHealth: getLastTimestamp(variables.healthData),
          }),
        },
      ],
    }),

    updateKetoMojoData: Model.buildEffect({
      name: `${this.namespace}/updateKetoMojoData`,
      dataPath: 'updateKetoMojoData',
      query: UPDATE_KETO_MOJO_DATA,
      *variables({ initial = false }: { initial: boolean }, { call, select }: any) {
        const ketoMojoUser: AuthorizeResult | undefined = yield select(ketoMojoUserSelector)
        const lastSyncedKetoMojo: string | null = yield select(ketoMojoLastSyncedSelector)
        const startDate: string =
          initial || !lastSyncedKetoMojo
            ? moment().subtract(1, 'year').format()
            : moment(lastSyncedKetoMojo).format()
        const endDate = moment().format()

        if (ketoMojoUser?.accessToken) {
          const data: Awaited<ReturnType<typeof KetoMojo.getReadings>> = yield call(
            KetoMojo.getReadings,
            startDate,
            endDate,
            ketoMojoUser.accessToken,
          )

          if (data) {
            return { data }
          }
        }
        return { data: [] }
      },
      isSuccess: (response: any) => response?.success,
      reducers: [
        {
          name: 'settings/updateIntegrationsSyncSettings',
          unless: (_: any, { variables }: { variables: { data: Reading[] } }) => !variables.data[0],
          payload: (_: any, { variables }: { variables: { data: Reading[] } }) => ({
            lastSyncedKetoMojo: variables.data[0].readingTimestamp,
          }),
        },
      ],
    }),

    exportData: Model.buildEffect({
      name: `${this.namespace}/exportData`,
      query: FETCH_DATA_EXPORT,
      isSuccess: (response: any) => get(response, 'dataExport.success'),
    }),

    config: Model.buildEffect({
      name: `${this.namespace}/config`,
      query: FETCH_CLIENT_CONFIG,
      reducers: [
        {
          name: 'updateAppState',
          payload: transform,
        },
      ],
    }),

    fetchCalendarData: Model.buildEffect({
      name: `${this.namespace}/fetchCalendarData`,
      query: FETCH_CALENDAR_DATA,
      caching: (variables) => Caching.shouldUseDateRangeCache(variables?.filter),
      cacheKey: (variables) => Caching.listFilterCacheKey('app/calendar', variables),
      reducers: [
        {
          name: 'updateCalendarState',
          payload: processCalendarData(calendarDateSelector),
        },
        {
          name: 'allEvents/updateCalendarState',
          payload: processCalendarData(allEventsCalendarDateSelector),
        },
      ],
    }),

    sendUserInvite: Model.buildEffect({
      name: `${this.namespace}/sendUserInvite`,
      query: SEND_USER_INVITE,
      dataPath: 'sendUserInvite',
      isSuccess: (response: any) => response?.success,
    }),

    sendIntercomUserMessage: Model.buildEffect({
      name: `${this.namespace}/sendIntercomUserMessage`,
      query: SEND_CHAT_CONVERSATION_MESSAGE,
      dataPath: 'sendChatConversationMessage',
      isSuccess: (response: any) => response?.success,
    }),

    createChatConversationNote: Model.buildEffect({
      name: `${this.namespace}/createChatConversationNote`,
      query: CREATE_CHAT_CONVERSATION_NOTE,
      dataPath: 'createChatConversationNote',
    }),

    clearCaches: Model.buildEffect({
      name: `${this.namespace}/clearCaches`,
      cacheReducers: [
        { name: 'activities/cacheClear' },
        { name: 'address/cacheClear' },
        { name: 'cacheClear' },
        { name: 'events/cacheClear' },
        { name: 'dailyCharts/cacheClear' },
        { name: 'dailyNutrition/cacheClear' },
        { name: 'ingredients/cacheClear' },
        { name: 'insights/cacheClear' },
        { name: 'meals/cacheClear' },
        { name: 'measurements/cacheClear' },
        { name: 'journalEntries/cacheClear' },
        { name: 'paymentMethod/cacheClear' },
        { name: 'scans/cacheClear' },
        { name: 'sensor/cacheClear' },
        { name: 'settings/cacheClear' },
        { name: 'settings/updateSettings', payload: { dailyMeasurementsSourcePriorities: {} } },
        { name: 'users/cacheClear' },
      ],
      warnings: false, // disable warnings as payload is empty
    }),

    surveyQuestionnaire: Model.buildEffect({
      name: `${this.namespace}/surveyQuestionnaire`,
      query: FETCH_QUESTIONNAIRE,
      dataPath: 'allSurveyLinks',
      caching: false,
    }),

    latestSurveyLinkByKind: Model.buildEffect({
      name: `${this.namespace}/latestSurveyLinkByKind`,
      query: FETCH_LATEST_SURVEY_BY_KIND,
      dataPath: 'latestSurveyLinkByKind',
      warnings: false, // disable warnings as response may be empty
      caching: false,
    }),

    startQuestionnaire: Model.buildEffect({
      name: `${this.namespace}/startQuestionnaire`,
      query: START_QUESTIONNAIRE,
      dataPath: 'startSurvey',
    }),

    saveSurveyResponse: Model.buildEffect({
      name: `${this.namespace}/saveSurveyResponse`,
      query: SAVE_SURVEY_RESPONSE,
      dataPath: 'saveSurveyResponse',
    }),

    saveSurveyResponses: Model.buildEffect({
      name: `${this.namespace}/saveSurveyResponses`,
      query: SAVE_SURVEY_RESPONSES,
      dataPath: 'saveSurveyResponses',
    }),

    completeSurvey: Model.buildEffect({
      name: `${this.namespace}/completeSurvey`,
      query: COMPLETE_SURVEY,
      caching: false,
    }),

    upsertUiState: Model.buildEffect({
      name: `${this.namespace}/upsertUiState`,
      query: UPSERT_UI_STATE,
      caching: false,
      reducers: [
        {
          name: 'updateUserState',
          *payload(response: UpsertUiStateMutation, { select }: any) {
            const newUiState = response.upsertUiState
            const uiStates: UiState[] = yield select(uiStatesSelector)
            if (uiStates.find((uiState) => uiState.name === newUiState.name)) {
              return {
                uiStates: uiStates.map((uiState) =>
                  uiState.name === newUiState.name ? newUiState : uiState,
                ),
              }
            }

            return {
              uiStates: [...uiStates, newUiState],
            }
          },
        },
      ],
    }),

    upsertDexcomConnection: Model.buildEffect({
      name: `${this.namespace}/upsertDexcomConnection`,
      query: UPSERT_DEXCOM_CONNECTION,
      reducers: [
        {
          name: 'updateUserState',
          payload: (
            response: UpsertDexcomConnectionMutation,
            { effectPayload }: { effectPayload: UpsertDexcomConnectionMutationVariables },
          ) => {
            const { dexcomConnectionKind } = effectPayload

            if (dexcomConnectionKind === DexcomConnectionKind.Stelo) {
              return { dexcomSteloConnection: response.upsertDexcomConnection }
            }

            return { dexcomConnection: response.upsertDexcomConnection }
          },
        },
        { name: 'users/fetch' },
      ],
    }),

    deleteDexcomConnection: Model.buildEffect({
      name: `${this.namespace}/deleteDexcomConnection`,
      query: DELETE_DEXCOM_CONNECTION,
      reducers: [
        {
          name: 'updateUserState',
          payload: (
            _: DeleteDexcomConnectionMutation,
            { effectPayload }: { effectPayload: DeleteDexcomConnectionMutationVariables },
          ) => {
            const { dexcomConnectionKind } = effectPayload

            if (dexcomConnectionKind === DexcomConnectionKind.Stelo) {
              return { dexcomSteloConnection: null }
            }

            return { dexcomConnection: null }
          },
        },
      ],
    }),
    refreshDataUponDexcomSync: Model.buildEffect({
      name: `${this.namespace}/refreshDataUponDexcomSync`,
      warnings: false, // disable warnings as payload is empty
      reducers: [{ name: 'dailyCharts/fetch' }, { name: 'dailyNutrition/fetch' }],
      cacheReducers: [
        { name: 'cacheClear' },
        { name: 'events/cacheClear', payload: { matchName: 'events/fetch' } },
        { name: 'insights/cacheClear' },
        { name: 'dailyCharts/cacheClear' },
        { name: 'dailyNutrition/cacheClear' },
      ],
    }),

    confirmLibreIndirectSync: Model.buildEffect({
      name: `${this.namespace}/confirmLibreIndirectSync`,
      query: CONFIRM_LIBRE_INDIRECT_SYNC,
      dataPath: 'confirmLibreIndirectSync',
      isSuccess: (response: { success: boolean }) => response.success === true,
    }),

    fetchUserFiles: Model.buildEffect({
      name: `${this.namespace}/fetchUserFiles`,
      query: FETCH_USER_FILES,
      dataPath: 'allFiles',
    }),

    fetchUserFile: Model.buildEffect({
      name: `${this.namespace}/fetchUserFile`,
      query: FETCH_USER_FILE,
      dataPath: 'file',
      warnings: false,
    }),

    createDirectUpload: Model.buildEffect({
      name: `${this.namespace}/createDirectUpload`,
      query: CREATE_DIRECT_UPLOAD,
      dataPath: 'createDirectUpload',
    }),

    createFileFromSignedId: Model.buildEffect({
      name: `${this.namespace}/createFileFromSignedId`,
      query: CREATE_FILE_FROM_SIGNED_ID,
      dataPath: 'createFileFromSignedId',
    }),

    deleteFile: Model.buildEffect({
      name: `${this.namespace}/deleteFile`,
      query: DELETE_FILE,
    }),

    fetchProducts: Model.buildEffect({
      name: `${this.namespace}/allProducts`,
      query: FETCH_PRODUCTS,
      dataPath: 'allProducts',
      reducers: [
        {
          name: 'fetchList',
          dataPath: 'products',
          storePath: (
            _: any,
            { effectPayload }: { effectPayload?: { category?: BillingProductCategory } },
          ) =>
            effectPayload?.category ? `productsByCategory.${effectPayload.category}` : 'products',
        },
      ],
    }),

    fetchProduct: Model.buildEffect({
      name: `${this.namespace}/product`,
      query: FETCH_PRODUCT,
      dataPath: 'product',
    }),

    workflowRun: Model.buildEffect({
      name: `${this.namespace}/workflowRun`,
      query: WORKFLOW_RUN,
      dataPath: 'workflowRun',
    }),
  }

  reducers = {
    ...Model.defaultReducers,
    ...reducers,
  }
}
